import { useEffect, useRef, useState } from 'react'
import { useInView } from 'motion/react'
import { useCompactViewport, useMediaQuery } from './useMediaQuery'

export interface CountUpProps {
  /** Final value, e.g. 12000. */
  value: number
  /** Printed before the number, e.g. "$". */
  prefix?: string
  /** Printed after the number, e.g. "+", "%". */
  suffix?: string
  /** Animation length in ms. */
  duration?: number
  className?: string
}

const EASE_OUT_EXPO = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t))

/**
 * A stat figure that counts up once when scrolled into view (taste-skill:
 * "a figure counts up once"). Renders the final value immediately on phones,
 * under prefers-reduced-motion, and in non-browser environments.
 */
export function CountUp({ value, prefix = '', suffix = '', duration = 1200, className }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const compact = useCompactViewport()
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const animate = !compact && !reduced
  // Start at the final value: environments where IntersectionObserver never
  // fires (jsdom, no-JS paints) must still show the real number. The reset to
  // 0 happens in the same effect tick that starts the count.
  const [display, setDisplay] = useState(value)

  useEffect(() => {
    if (!animate) {
      setDisplay(value)
      return
    }
    if (!inView) return
    let frame: number
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      setDisplay(Math.round(value * EASE_OUT_EXPO(t)))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [animate, inView, value, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  )
}

export default CountUp
